import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchListings } from '../../../utility/firebaseHelpers';
import { SearchParams } from '../BrowseListingsPage';
import { Listing } from '../../../components/property/property-card/PropertyCardTypes';
import { useAnalyticsWithConsent } from '../../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../../analytics/firebaseAnalytics';
import toast from 'react-hot-toast';

interface UseSearchListingsResult {
  listings: Listing[];
  isLoading: boolean;
  currentPage: number;
  totalListings: number;
  sortOption: string;
  searchParams: SearchParams;
  totalPages: number;
  searchStartTime: React.MutableRefObject<Date | undefined>;
  searchHistory: React.MutableRefObject<SearchParams[]>;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  setSortOption: (option: string) => void;
  setCurrentPage: (page: number) => void;
  handleSearch: (params: SearchParams) => void;
  handleSort: (option: string) => void;
  handlePageChange: (newPage: number) => void;
  loadListings: (
    page: number,
    params: SearchParams,
    sort: string
  ) => Promise<void>;
}

export const useSearchListings = (
  initialSearchParams?: Partial<SearchParams>
): UseSearchListingsResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const analytics = useAnalyticsWithConsent();

  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalListings, setTotalListings] = useState(0);
  const [sortOption, setSortOption] = useState('bestMatch');

  const searchStartTime = useRef<Date>();
  const searchHistory = useRef<SearchParams[]>([]);

  // Memoize initial search params to avoid recreating object on every render
  const memoizedInitialParams = useMemo(() => {
    const merged = {
      location: '',
      minPrice: 0,
      maxPrice: 0,
      bedrooms: 0,
      bathrooms: 0,
      propertyType: 'Any' as const,
      berRating: 'Any',
      availableFrom: null,
      listingIntent: 'Any' as const,
      ...initialSearchParams
    };
    return merged;
  }, [initialSearchParams]);

  const [searchParams, setSearchParams] = useState<SearchParams>(
    memoizedInitialParams
  );

  const listingsPerPage = 16;
  const totalPages = Math.ceil(totalListings / listingsPerPage);

  const parseQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const parsed = {
      location: params.get('location') || '',
      minPrice: parseInt(params.get('minPrice') || '0'),
      maxPrice: parseInt(params.get('maxPrice') || '0'),
      bedrooms: parseInt(params.get('bedrooms') || '0'),
      bathrooms: parseInt(params.get('bathrooms') || '0'),
      propertyType: (params.get('propertyType') as any) || 'Any',
      berRating: params.get('berRating') || 'Any',
      availableFrom: params.get('availableFrom')
        ? new Date(params.get('availableFrom')!)
        : null,
      listingIntent: (params.get('listingIntent') as any) || 'Any'
    };
    return parsed;
  }, [location.search]);

  const updateQueryParams = useCallback(
    (params: SearchParams) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== '' && value !== 'Any' && value !== 0) {
          if (key === 'availableFrom' && value instanceof Date) {
            searchParams.append(key, value.toISOString());
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true
      });
    },
    [navigate, location.pathname]
  );

  const loadListings = useCallback(
    async (page: number, params: SearchParams, sort: string) => {
      setIsLoading(true);
      try {
        const { listings, total } = await fetchListings(
          page,
          listingsPerPage,
          params,
          sort
        );
        setListings(listings);
        setTotalListings(total);
      } catch (error) {
        console.error('Failed to load listings:', error);
        toast.error('Failed to load listings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [listingsPerPage]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage === currentPage || isLoading) return;

    analytics.logEvent(AnalyticsEvents.PAGINATION_INTERACTION, {
      previous_page: currentPage,
      new_page: newPage,
      results_per_page: listingsPerPage,
      total_pages: totalPages,
      sort_option: sortOption,
      search_params: searchParams
    });

    setCurrentPage(newPage);
    loadListings(newPage, searchParams, sortOption);

    // Scroll to top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSearch = (params: SearchParams) => {
    searchStartTime.current = new Date();
    searchHistory.current.push(params);

    analytics.logEvent('property_search', {
      search_params: {
        location: params.location,
        price_range:
          params.maxPrice > 0 ? `${params.minPrice}-${params.maxPrice}` : 'any',
        bedrooms: params.bedrooms || 'any',
        bathrooms: params.bathrooms || 'any',
        property_type: params.propertyType,
        listing_intent: params.listingIntent,
        ber_rating: params.berRating,
        available_from: params.availableFrom?.toISOString()
      },
      search_refinement: searchHistory.current.length > 1,
      previous_search_count: searchHistory.current.length - 1
    });

    setSearchParams(params);
    setCurrentPage(1);
    updateQueryParams(params);
    loadListings(1, params, sortOption);
  };

  const handleSort = (option: string) => {
    analytics.logEvent(AnalyticsEvents.SEARCH_SORT, {
      sort_option: option,
      previous_sort: sortOption,
      results_count: totalListings,
      page_number: currentPage
    });

    setSortOption(option);
    setCurrentPage(1);
    loadListings(1, searchParams, option);
  };

  // Initialize from URL params on mount and when URL changes
  useEffect(() => {
    const urlParams = parseQueryParams();

    // Smart merge: only use URL params if they have non-default values
    // Otherwise, preserve initialSearchParams values
    const mergedParams: SearchParams = {
      location: urlParams.location || memoizedInitialParams.location,
      minPrice: urlParams.minPrice || memoizedInitialParams.minPrice,
      maxPrice: urlParams.maxPrice || memoizedInitialParams.maxPrice,
      bedrooms: urlParams.bedrooms || memoizedInitialParams.bedrooms,
      bathrooms: urlParams.bathrooms || memoizedInitialParams.bathrooms,
      propertyType:
        urlParams.propertyType !== 'Any'
          ? urlParams.propertyType
          : memoizedInitialParams.propertyType,
      berRating:
        urlParams.berRating !== 'Any'
          ? urlParams.berRating
          : memoizedInitialParams.berRating,
      availableFrom:
        urlParams.availableFrom || memoizedInitialParams.availableFrom,
      listingIntent:
        urlParams.listingIntent !== 'Any'
          ? urlParams.listingIntent
          : memoizedInitialParams.listingIntent
    };
    setSearchParams(mergedParams);
    setCurrentPage(1);
    loadListings(1, mergedParams, sortOption);
  }, [
    location.search,
    memoizedInitialParams,
    parseQueryParams,
    loadListings,
    sortOption
  ]);

  return {
    listings,
    isLoading,
    currentPage,
    totalListings,
    sortOption,
    searchParams,
    totalPages,
    searchStartTime,
    searchHistory,
    setSearchParams,
    setSortOption,
    setCurrentPage,
    handleSearch,
    handleSort,
    handlePageChange,
    loadListings
  };
};
